import { Badge } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import type { GeoJSONSource } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type MapGeoJSONFeature, useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { CIVIL_DRAW_ROUTE } from '~/common/types';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import { useCustomDataCategoryLabels } from '~/pages/projectDetails/map/useCustomDataCategoryLabels';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { TOCLegendParentMap } from '~/store2/TOCLayers';
import type { MapStoreBase } from '../..';
import FeatureStateHighlight from '../FeatureStateHighlight';
import { MAP_CLICK_REFERENCE_SOURCE, MAP_HOVER_HIGHLIGHT_SOURCE } from '../HighlightLayer';
import {
  buildSidebarSections,
  type FeatureRef,
  type SidebarFieldRow,
  type SidebarSection,
} from './buildSidebarSections';
import MetadataSection from './MetadataSection';
import {
  calcBoundingBox,
  describeSelection,
  getIdentifiableLayerIds,
  NO_FEATURES_FOUND_MESSAGE,
  resolveSidebarSections,
  selectFeatureStateTarget,
  selectHighlightTarget,
  selectVisibleSections,
  shouldPersistCache,
} from './metadataSidebar.helpers';
import { captureMetadataLinkOpened } from './metadataSidebarAnalytics';

// Single source of truth for the docked panel's width. Map.tsx offsets its top-right
// controls and attribution by this (+16px gutter) so they clear the panel.
export const METADATA_SIDEBAR_WIDTH = 360;

interface Props {
  store: MapStoreBase;
}

export default function MetadataSidebar({ store }: Props) {
  const { current: map } = useMap();
  const { identifyDetailsPoint, selectedFeatureKey, identifyDetailsSectionsCache } = useSnapshot(store);
  const { tilesConfig, visibleLayerIds, TOCLayers } = useSnapshot(layerStore) as typeof layerStore;
  const { tabId, projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const customDataCategoryLabels = useCustomDataCategoryLabels(projectId);

  const panelRef = useRef<HTMLElement>(null);
  const [hovered, setHovered] = useState<{
    geometry: GeoJSON.Geometry;
    color?: string;
    featureRef?: FeatureRef;
  } | null>(null);

  // Clear any hover highlight when the identify point changes (panel close / re-identify),
  // since a mouseleave may never fire in those cases.
  // biome-ignore lint/correctness/useExhaustiveDependencies: identifyDetailsPoint is the intentional re-run trigger; the body clears hover state.
  useEffect(() => {
    setHovered(null);
  }, [identifyDetailsPoint]);

  // Identify: query the rendered features under the click point. Kept pure (no setState /
  // analytics / highlight writes) so it's safe to recompute.
  // Do NOT add visibleLayerIds/activeLayerIds to deps: a re-run at a registered-but-unpainted
  // instant would show an authoritative-looking empty, because resolveSidebarSections trusts
  // liveSections whenever mapReady is true (CG-850, CG-920).
  // biome-ignore lint/correctness/useExhaustiveDependencies: visibleLayerIds/activeLayerIds read at click-time only
  const { features, mapReady } = useMemo<{ features: MapGeoJSONFeature[]; mapReady: boolean }>(() => {
    if (!map || !identifyDetailsPoint || Number.isNaN(identifyDetailsPoint.lng)) {
      return { features: [], mapReady: false };
    }
    // visibleLayerIds is the tracked snapshot value; activeLayerIds is read from the proxy so a
    // layer toggle doesn't re-run this query while the panel is open (deps are point/map only).
    const queryLayerIds = getIdentifiableLayerIds(map, { visibleLayerIds, activeLayerIds: layerStore.activeLayerIds });
    const features = map.queryRenderedFeatures(calcBoundingBox(map.project(identifyDetailsPoint), 10), {
      layers: queryLayerIds,
    });
    return { features, mapReady: queryLayerIds.length > 0 };
  }, [identifyDetailsPoint, map]);

  const { sections: liveSections, errors } = useMemo(
    () =>
      buildSidebarSections(features, {
        tilesConfig,
        toc: TOCLayers,
        legendParentMap: TOCLegendParentMap,
        includeFieldsToCopy: tabId === CIVIL_DRAW_ROUTE,
        customDataCategoryLabels,
      }),
    [features, tilesConfig, TOCLayers, tabId, customDataCategoryLabels],
  );

  // Render from cache when the query ran without a ready layer set (mapReady=false), so the
  // closing-panel remount race (Project Info, Downloads, Proposal Packet) can't show an
  // authoritative-looking "0 in area" (CG-850).
  const sections = resolveSidebarSections({
    mapReady,
    liveSections,
    point: identifyDetailsPoint,
    cache: identifyDetailsSectionsCache,
    projectId,
  });

  // Persist the sections resolved while the map WAS ready, keyed on the point + projectId, so a
  // later remount has something valid to fall back to. Skipping on !mapReady or on empty sections
  // keeps a race-empty pass from ever overwriting a good cache (CG-850, CG-920).
  useEffect(() => {
    // No project to key the cache on — skip rather than write an unkeyed entry (CG-920).
    if (!projectId) return;
    if (!shouldPersistCache({ mapReady, point: identifyDetailsPoint, sections: liveSections })) return;
    store.setIdentifyDetailsSectionsCache({
      // shouldPersistCache already checked point is defined/non-NaN above.
      point: identifyDetailsPoint as { lng: number; lat: number },
      sections: liveSections,
      projectId,
    });
  }, [mapReady, identifyDetailsPoint, liveSections, store, projectId]);

  // Glow highlight: show the hovered card's feature, or — in left-click single-select — the
  // selected feature persistently. A target with a live vector-tile reference paints the WHOLE
  // feature via feature state (FeatureStateHighlight, rendered below); any target without a
  // featureRef keeps the tile-clipped GeoJSON fragment on the shared glow source. Cleared when
  // neither applies / on unmount.
  // Both memoized on their real inputs: they are the deps of the effect below and the prop of
  // FeatureStateHighlight, so fresh object literals every render would churn
  // setFeatureState/removeFeatureState on any unrelated re-render.
  const target = useMemo(
    () => selectHighlightTarget(sections, selectedFeatureKey, hovered),
    [sections, selectedFeatureKey, hovered],
  );
  // null here sends the target down the GeoJSON fragment path below and renders
  // FeatureStateHighlight as nothing.
  const featureStateTarget = useMemo(() => selectFeatureStateTarget(target), [target]);

  // What the panel actually renders: left-click single-select narrows to one section
  // ("1 selected"); right-click / custom-data (no key) shows every section ("N in area").
  //
  // The feature-state stacks mount from THIS, not from the full `sections` list: a card the user
  // cannot see can be neither hovered nor the selected target, so any source outside it would
  // mount a stack that can never paint — and each stack's first layer makes mapbox-gl mark that
  // source's cache 'reload' and re-parse every live tile of it. In single-select that is one
  // source instead of every source under the click point; in area mode this IS `sections`.
  //
  // Memoized alongside the targets above, and above the early return so the hook order is fixed:
  // the single-select branch returns a fresh `[match]` array every render, and
  // FeatureStateHighlight memoizes its source list on this prop's identity. The GeoJSON highlight
  // effect below and the analytics still key off the full `sections` list.
  const { visible, chipLabel } = useMemo(
    () => selectVisibleSections(sections, selectedFeatureKey),
    [sections, selectedFeatureKey],
  );

  useEffect(() => {
    const src = map?.getSource(MAP_HOVER_HIGHLIGHT_SOURCE) as GeoJSONSource | undefined;
    src?.setData({
      type: 'FeatureCollection',
      features:
        target && !featureStateTarget
          ? [{ type: 'Feature', geometry: target.geometry, properties: target.color ? { color: target.color } : {} }]
          : [],
    });
    return () => {
      src?.setData({ type: 'FeatureCollection', features: [] });
    };
  }, [target, featureStateTarget, map]);

  // Reference circle: mark the clicked point in AREA mode only ("N in area"), where it ties the
  // list back to where the user clicked. In single-select (selectedFeatureKey set) the user picked
  // ONE feature, so a where-you-clicked circle is misleading — clear it. Also cleared on close/unmount.
  useEffect(() => {
    const src = map?.getSource(MAP_CLICK_REFERENCE_SOURCE) as GeoJSONSource | undefined;
    src?.setData({
      type: 'FeatureCollection',
      features:
        identifyDetailsPoint && !Number.isNaN(identifyDetailsPoint.lng) && !selectedFeatureKey
          ? [
              {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [identifyDetailsPoint.lng, identifyDetailsPoint.lat] },
                properties: {},
              },
            ]
          : [],
    });
    return () => {
      src?.setData({ type: 'FeatureCollection', features: [] });
    };
  }, [identifyDetailsPoint, selectedFeatureKey, map]);

  // Analytics: fire once per real identify (keyed on the point), no dedup ref needed. Skips on
  // !mapReady — true for the CG-850 remount race, but also whenever every identifiable layer is
  // toggled off, so a right-click identify with no active layers won't fire the open event (accepted).
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per identify
  useEffect(() => {
    if (!mapReady) return;
    if (!identifyDetailsPoint || Number.isNaN(identifyDetailsPoint.lng)) return;

    captureTypedPosthogEvent(POSTHOG_EVENTS.projectMetadataOpened, {
      ...(projectId && { project_id: projectId }),
      organization_id: rootStore.currentOrganizationId || undefined,
      metadata_source: 'project_info',
    });

    // buildSidebarSections now emits one section per feature, so a layer can appear across
    // several sections. Fire `layer_detail` once per distinct layerId to avoid over-firing.
    const seenLayerIds = new Set<string>();
    for (const section of sections) {
      if (section.isCustomData) {
        if (!projectId) continue;
        // Analytics must use the RAW CateTitle (not the humanized display string) so the
        // PostHog `category` dimension stays a single, stable format — see buildSidebarSections.
        for (const category of section.customCategoriesRaw ?? []) {
          captureTypedPosthogEvent(POSTHOG_EVENTS.customDataFeatureInspected, {
            project_id: projectId,
            organization_id: rootStore.currentOrganizationId || undefined,
            layer_id: section.id,
            category,
          });
        }
      } else {
        if (seenLayerIds.has(section.layerId)) continue;
        seenLayerIds.add(section.layerId);
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectMetadataOpened, {
          ...(projectId && { project_id: projectId }),
          organization_id: rootStore.currentOrganizationId || undefined,
          metadata_source: 'layer_detail',
          layer_id: section.layerId,
        });
      }
    }

    for (const err of errors) {
      capturePosthogEvent(POSTHOG_EVENTS.mapMetadataLayerError, {
        layer_id: err.layerId,
        config_key: err.configKey,
        error: err.message,
      });
    }
  }, [identifyDetailsPoint, mapReady]);

  // A11y: move focus into the panel each time a new point opens it, and restore focus to
  // whatever had it beforehand when the panel closes/unmounts. Non-modal, no focus trap.
  useEffect(() => {
    if (identifyDetailsPoint && !Number.isNaN(identifyDetailsPoint.lng)) {
      const previouslyFocused = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
      return () => {
        // Opened from the map's context menu, the element that had focus is the removed menu item.
        if (previouslyFocused?.isConnected) previouslyFocused.focus();
        else map?.getCanvas().focus();
      };
    }
  }, [identifyDetailsPoint, map]);

  // Announce each identify result once per point. Not gated on mapReady, so identifying with no layers on
  // still speaks the empty result.
  // biome-ignore lint/correctness/useExhaustiveDependencies: announce once per identify, not on every sections recompute
  useEffect(() => {
    const point = store.identifyDetailsPoint;
    if (!point || Number.isNaN(point.lng)) return;
    store.announceSelectionFor(point, describeSelection(sections, selectedFeatureKey));
  }, [identifyDetailsPoint]);

  if (!map || !identifyDetailsPoint || Number.isNaN(identifyDetailsPoint.lng)) return null;

  const onBookmark = (section: SidebarSection) => {
    store.copyMetadataToCanvas(section.title, {
      map,
      fields: section.fieldsToCopy ?? [],
      lngLat: identifyDetailsPoint,
      color: section.color ?? '#000000',
      minZoom: section.minZoom ?? 17,
    });
  };

  const onLinkOpen = (section: SidebarSection, row: SidebarFieldRow) =>
    captureMetadataLinkOpened({ projectId, organizationId: rootStore.currentOrganizationId }, section, row);

  return (
    <>
      <FeatureStateHighlight sections={visible} target={featureStateTarget} />
      <section
        ref={panelRef}
        tabIndex={-1}
        // Non-modal docked panel: a <section> with an aria-label is a `region` landmark natively
        // (not `dialog`, which would imply a modal focus trap).
        aria-label="Layer Attributes"
        data-testid="metadata-sidebar"
        onKeyDown={(e) => {
          if (e.key === 'Escape') store.setIdentifyDetailsPoint(undefined);
        }}
        style={{ width: METADATA_SIDEBAR_WIDTH }}
        className="pointer-events-auto absolute right-0 top-0 z-30 flex h-full max-w-[85%] flex-col border-l border-grey-30 bg-white shadow-[-6px_0_18px_rgba(31,44,56,0.1)]"
      >
        <div className="flex items-center justify-between border-b border-grey-30 px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-grey-90">Layer Attributes</span>
            <Badge color="grey">{chipLabel}</Badge>
          </div>
          <CloseIconButton
            aria-label="Close layer attributes"
            data-testid="metadata-sidebar-close"
            onClick={() => store.setIdentifyDetailsPoint(undefined)}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="flex h-full items-center justify-center p-4 text-center text-sm text-grey-50">
              {NO_FEATURES_FOUND_MESSAGE}
            </div>
          ) : (
            visible.map((section) => (
              <MetadataSection
                key={section.id}
                section={section}
                onBookmark={onBookmark}
                onLinkOpen={onLinkOpen}
                onHover={(s) =>
                  setHovered(
                    s.geometry
                      ? {
                          geometry: s.geometry,
                          color: s.coreColor,
                          ...(s.featureRef ? { featureRef: s.featureRef } : {}),
                        }
                      : null,
                  )
                }
                onHoverEnd={() => setHovered(null)}
              />
            ))
          )}
        </div>
      </section>
    </>
  );
}
